import { Yii } from "libraries/interfaces/Yii";
import {
    ApplicationRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GenericForm } from '../generic-form';
import { Config } from './config';
import { travellerGroupInArray } from '../utils/groups/traveller-group';
import { FormBuilderService } from "libraries/service/formBuilder/formBuilder.service";
import { CardFormComponent, IOptionsCardForm } from "../card-form/card-form.component";
import { IValidator } from "../utils/validators/IValidator";
import { ITravellerObject } from "libraries/service/resources/hotel/ISearchParameters";
import * as moment from 'moment';
import {
    TravellerFieldsetComponent
} from "libraries/module/formbuilder/fieldsets/traveller-fieldset/traveller-fieldset.component";
import { ILabels } from "libraries/interfaces/ILabels";
import { ETravellerTitle } from "libraries/interfaces/ITraveller";
import { EServiceQualityEnum, IServiceQuality } from "libraries/service/resources/airQuote/airQuoteResource.service";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { ActionInBookingType, ActionType } from "@afidium/ts-facade";
import { ActionQualityHelperService } from "libraries/service/action-quality-helper/action-quality-helper.service";

export interface IAgeConfig {
    default: number;
    min: number;
    max: number;
}

export interface IAgesConfig {
    adult: {
        age: IAgeConfig
    };
    child: {
        age: IAgeConfig
    };
    infant: {
        age: IAgeConfig
    };
    journeyDate?: Date;
}

interface ITravellerInput {
    xId: string;
    id?: string;
    birthDate?: Date | string;
    type: 'adult' | 'child' | 'infant' | 'Adult' | 'Child' | 'Infant';
}

/**
 * Search-form
 * Generate a fieldset of needed fields
 * @extends GenericForm
 */
@Component({
    selector: 'travellerForm',
    templateUrl: './travellerForm.component.html',
    styles: [`
        traveller-fieldset ::ng-deep .list-row {
            padding-top: 12px;
        }

        .card-body-content {
            padding-bottom: 12px;
            padding-top: 1px;
        }

        notification {
            margin: .75rem .75rem 0 .75rem;
        }
    `],
})
export class TravellerFormComponent extends GenericForm implements OnInit, OnChanges, OnDestroy {

    @Input()
    public formConfig: IOptionsCardForm = {
        formGroup: null,
        labels: {
            error: Yii.t('wui-shared-js', 'Certains champs requis ne sont pas correctement remplis')
        }
    };

    @Input()
    public config: Config = Object.freeze(new Config());

    @Input()
    public nbItems: number;

    @Input()
    public travellerItem: ITravellerObject[] | ITravellerObject;

    @Input()
    public travellerList: any = {};

    //used to show next traveller Form
    @Input()
    public index: any = {};

    @Input()
    public useApis: boolean;

    //used to hide last 'Valider et suivant'
    @Input()
    public last: any = {};


    @Input()
    public columns: boolean;

    @Input()
    public disableTypes: boolean = false;

    @Input()
    public validators: {
        [string: string]: IValidator
    };

    @Input()
    public ageLimitConfig: IAgesConfig =
        {
            adult: {
                age: {
                    default: 30,
                    min: 18,
                    max: 100
                }
            },
            child: {
                age: {
                    default: 12,
                    min: 2,
                    max: 18
                }
            },
            infant: {
                age: {
                    default: 0,
                    min: 0,
                    max: 2
                }
            }
        };

    @Input()
    public actionArray: ActionType[];

    @Input()
    public contextIsModifySegment: boolean = false;

    @Output()
    public readonly onGetTravellerInformations: EventEmitter<any> = new EventEmitter();

    @Output('onSubmit')
    public readonly onSubmitTraveller: EventEmitter<FormGroup> = new EventEmitter();

    @ViewChild('cardFormTemplate')
    public cardFormTemplate: CardFormComponent;
    /**
     * Every ViewChild is template
     * With this we give the possibilities to display the template we want dynamically
     * Every template (fieldset) as to have a view child reference
     */
    @ViewChild('traveller')
    public traveller: TemplateRef<HTMLElement>;

    @ViewChild('travellerFieldsetComponent')
    public travellerFieldsetComponent: TravellerFieldsetComponent;

    public readonly wording: ILabels = {
        traveller: Yii.t('wui-shared-js', 'Voyageur'),
        travellers: Yii.t('wui-shared-js', 'Voyageurs'),
    };

    /** This is the initial */
    public form: FormGroup;
    /** DI - FormBuilder */
    public name: string = "travellerForm";

    public apiServiceQualityMessage: string[];

    public apisNotification: INotification = {
        type: NotificationType.Warning,
        active: false,
    };

    private subscription: Subscription;

    constructor(public fb: FormBuilder,
                public formBuilderService: FormBuilderService,
                protected applicationRef: ApplicationRef,
                protected readonly actionQualityHelperService: ActionQualityHelperService,
    ) {
        super(fb, formBuilderService);
    }

    /**
     * Init the form model
     */
    public ngOnInit(): void {
        this.form = this.fb.group({
            ...this.constructForm()
        });
        if (Array.isArray(this.travellerItem)) {
            this.travellerItem = this.travellerItem.map((traveller: ITravellerObject): ITravellerObject => ({
                ...traveller,
                title: this.initTitleValue(traveller.title),
            }));
        }

        const travellersList: ITravellerObject[] = this.travellerItem && (this.travellerItem as ITravellerObject[]).length ?
            this.travellerItem as ITravellerObject[] :
            this.travellerItem && [this.travellerItem as ITravellerObject] || [];

        if (travellersList.length > 1) {
            for (let i: number = 1; i < travellersList.length; i++) {
                const travellerFormGroup = this.getTravellerFormGroup();
                const form = this.form.get('traveller') as FormArray;
                form.push(travellerFormGroup);
            }
        }

        const travellerInputs: ITravellerInput[] = travellersList
            .map((traveller: ITravellerObject) => {

                const passEndValue: Date | string = traveller.passport?.validity?.end || traveller.passport?.validity?.passEnd;
                const cardEndValue: Date | string = traveller.identityCard?.validity?.end || traveller.identityCard?.validity?.passEnd;
                return ({
                    ...traveller,
                    identityType: traveller.identityType ? traveller.identityType :
                        traveller.passport ? 'passport' : traveller.identityCard ? "identityCard" : null,
                    birthDate: traveller.birthDate && typeof traveller.birthDate === "string"
                        ? moment(traveller.birthDate, "YYYY-MM-DD").toDate() : traveller.birthDate || undefined,
                    // #27875 modify data from json
                    passport: traveller.passport ? {
                        passNumber: (traveller.passport.number || traveller.passport.passNumber),
                        issuance: traveller.passport.issuance ? {
                            passCountry: (traveller.passport.issuance.country || traveller.passport.issuance.passCountry),
                        } : {},
                        validity: traveller.passport.validity ? {
                                passEnd: passEndValue && typeof passEndValue === "string"
                                    ? moment(passEndValue, "YYYY-MM-DD").toDate() : passEndValue || undefined,
                            } :
                            {},
                    } : {},
                    passportCitizenship: traveller.passport ? (traveller.citizenship || traveller.passportCitizenship) : null,
                    identityCardCitizenship: traveller.identityCard ? (traveller.citizenship || traveller.identityCardCitizenship) : null,
                    identityCard: traveller.identityCard ? {
                        cardNumber: (traveller.identityCard.number || traveller.identityCard.cardNumber),
                        issuance: traveller.identityCard.issuance ? {
                            cardCountry: (traveller.identityCard.issuance.country || traveller.identityCard.issuance.cardCountry),
                        } : {},
                        validity: traveller.identityCard.validity ? {
                            cardEnd: cardEndValue && typeof cardEndValue === "string"
                                ? moment(cardEndValue, "YYYY-MM-DD").toDate() : cardEndValue || undefined,
                        } : {},
                    } : {},
                });
            });
        this.form.get('traveller').patchValue(travellerInputs);

        this.formConfig = {
            ...this.formConfig,
            formGroup: this.form
        };

        this.subscription = this.formBuilderService
            .info
            .subscribe(
                (_data: string): void => {
                    if (_data === 'traveller') {
                        this.getData(this.form);
                    }
                });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.travellerItem && this.form &&
            changes.travellerItem.currentValue !== changes.travellerItem.previousValue) {
            this.form.patchValue(this.travellerItem);
        }
        if (changes?.actionArray?.currentValue) {
            this.initApisRules();
        }
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    /** get form values after blur */
    public getData(_form: any): void {
        this.formBuilderService.emitSubmitFormWithName('travellerForm', this.getFormData(_form));
    }

    public getFormData(form: FormGroup): any {
        return this.removeEmpty(form.getRawValue());
    }


    /**
     * onSubmit call service to send request to API
     * @param form : values of fields, valid: a boolean after checking Validators
     */
    public onFormSubmitTraveller(form: FormGroup): void {
        this.onSubmitTraveller.emit(form);
    }

    public submitForm(): void {
        this.cardFormTemplate.submit();
        this.travellerFieldsetComponent.onParentFormCheck();
    }

    public getTravellerInformations(form: FormGroup): void {
        if (form.valid) {
            this.onGetTravellerInformations.emit(form.getRawValue());
        }
    }

    public displayErrors(): void {
        //this.applicationRef.tick();
    }

    public isValid(): boolean {
        return this.form.valid;
    }

    protected getTravellerFormGroup(): FormGroup {
        return travellerGroupInArray(this.fb);
    }

    private initTitleValue(title: string): ETravellerTitle {
        if (title && (title.toLowerCase() === 'mrs' || title.toLowerCase() === 'ms')) {
            return ETravellerTitle.Ms;
        }

        return ETravellerTitle.Mr;
    }

    private initApisRules(): void {
        const apisAction: ActionInBookingType = this.actionQualityHelperService
            .extractActionByQualificationForQuotedAvailabilityTrip(this.actionArray, EServiceQualityEnum.APIS);

        if (apisAction && !apisAction.xMatch && apisAction.textLineArray?.length) {
            const message: string = apisAction.textLineArray[0] === 'SFPD' ? "Les données SFPD sont obligatoires." :
                apisAction.textLineArray[0] === 'APIS' ? "Les données passeport ou CNI sont obligatoires." :
                    "";
            this.apiServiceQualityMessage = apisAction.textLineArray as string[];
            this.apisNotification = {
                ...this.apisNotification,
                active: true,
                message
            };
        }
    }
}
